/**
 * Debounced, throttled, cached username-availability checker for the
 * signup + profile-settings forms.
 *
 * Production-grade properties:
 *  - **Debounce**: a check waits `DEBOUNCE_MS` before touching the
 *    network, so typing "aleksa" fires one request, not six.
 *  - **Latest-wins / throttle**: a monotonic token means only the most
 *    recent keystroke actually calls the API; superseded checks resolve
 *    early without a network call.
 *  - **Cancel stale responses**: if a response comes back after the user
 *    has typed more, it's discarded (can't flash a stale error).
 *  - **Cache (TTL)**: repeated checks of the same slug (e.g. typing then
 *    deleting back to it) are served from cache; entries expire so a
 *    name taken by someone else isn't cached "available" forever.
 *  - **Fail-open**: any API/network error resolves to available so a
 *    backend hiccup never blocks signup (the server is the source of
 *    truth at submit anyway, and uniqueness is best-effort).
 *
 * Returns `{ available: boolean, reason: string|null }`. Sync checks
 * (format, length, reserved words) are NOT done here — callers run those
 * instantly via util/username before ever calling this.
 */

import { checkUsername } from './api';

const DEBOUNCE_MS = 450;
const CACHE_TTL_MS = 60 * 1000;
const CACHE_MAX = 200;

// slug -> { result, at }
const cache = new Map();
let latestToken = 0;

const getCached = slug => {
  const hit = cache.get(slug);
  if (!hit) {
    return null;
  }
  if (Date.now() - hit.at > CACHE_TTL_MS) {
    cache.delete(slug);
    return null;
  }
  return hit.result;
};

const setCached = (slug, result) => {
  cache.set(slug, { result, at: Date.now() });
  if (cache.size > CACHE_MAX) {
    const oldest = cache.keys().next().value;
    cache.delete(oldest);
  }
};

const SUPERSEDED = { available: true, reason: 'superseded' };

/**
 * @param {string} slug Normalized username (already slugified).
 * @returns {Promise<{ available: boolean, reason: string|null }>}
 */
export const checkUsernameAvailability = slug => {
  if (!slug) {
    return Promise.resolve({ available: false, reason: 'invalid' });
  }
  const cached = getCached(slug);
  if (cached) {
    return Promise.resolve(cached);
  }

  const token = ++latestToken;
  return new Promise(resolve => {
    setTimeout(() => {
      // Debounce window elapsed — bail if a newer keystroke superseded us.
      if (token !== latestToken) {
        resolve(SUPERSEDED);
        return;
      }
      checkUsername({ username: slug })
        .then(res => {
          // Discard a response the user has already typed past.
          if (token !== latestToken) {
            resolve(SUPERSEDED);
            return;
          }
          const result = { available: !!res?.available, reason: res?.reason || null };
          setCached(slug, result);
          resolve(result);
        })
        .catch(() => resolve({ available: true, reason: 'check-unavailable' }));
    }, DEBOUNCE_MS);
  });
};

// Test seam.
export const __clearUsernameAvailabilityCache = () => {
  cache.clear();
  latestToken = 0;
};
